import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BrandLockup from "./BrandLockup";

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Essentials", href: "#collection" },
  { label: "Airport Pilot", href: "#airport-pilot" },
  { label: "Campus + Living", href: "#campus-pilot" },
  { label: "The Concept", href: "#concept" },
  { label: "Locations", href: "#locations" },
  { label: "Why MinisRus", href: "#why" },
  { label: "About Us", href: "#about" },
  { label: "Contact", href: "#enquiries" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const smoothTo = (href) => {
    if (href === "#top") window.scrollTo({ top: 0, behavior: "smooth" });
    else { const el = document.querySelector(href); el && el.scrollIntoView({ behavior: "smooth", block: "start" }); }
    setOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-ink/85 border-b border-white/8" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 md:px-10 h-20 md:h-24">
        <a
          href="#top"
          onClick={(e) => { e.preventDefault(); smoothTo('#top'); }}
          data-testid="brand-logo"
          aria-label="MinisRus home"
          className="flex items-center"
        >
          <BrandLockup />
        </a>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => smoothTo(n.href)}
              className="text-[13px] text-bone/85 hover:text-orange link-underline transition-colors"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => smoothTo('#enquiries')}
            className="hidden md:inline-flex items-center gap-2 h-11 px-6 bg-orange text-ink hover:bg-orange-soft rounded-full text-[13px] font-semibold tracking-wide transition-all"
            data-testid="header-partner-btn"
          >
            Request a Pilot
            <span className="inline-block">→</span>
          </button>
          <button
            className="xl:hidden text-bone p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.77, 0, 0.18, 1] }}
            className="xl:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-white/8"
          >
            <div className="px-5 py-6 flex flex-col gap-3">
              {NAV.map((n) => (
                <button
                  key={n.label}
                  onClick={() => smoothTo(n.href)}
                  className="text-left text-lg text-bone hover:text-orange transition-colors py-2 border-b border-white/5"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => smoothTo('#enquiries')}
                className="mt-3 h-12 bg-orange text-ink rounded-full text-[13px] font-semibold"
                data-testid="mobile-partner-btn"
              >
                Request a Pilot →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
